import { menuAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe, type SystemStyleObject } from '@chakra-ui/react/styled-system';

const itemActive: SystemStyleObject = {
  background: 'button/secondary/bg-active',
  _icon: {
    color: 'icon/primary',
  },
};
const itemActiveDanger: SystemStyleObject = {
  background: 'button/danger-secondary/bg-active',
  color: 'button/danger-secondary/fg-active',
  _icon: {
    color: 'button/danger-secondary/fg-active',
  },
};
const menuSlotRecipe = defineSlotRecipe({
  className: 'menu',
  slots: [...menuAnatomy.keys(), 'itemHelper'],
  variants: {
    size: {
      lg: {
        item: {
          alignItems: 'center',
          paddingY: '12',
          textStyle: 'body/lg/regular',
          // With a helper line the item aligns to the top of the label and gets tighter padding.
          '&[data-helper]': {
            alignItems: 'flex-start',
            paddingY: '8',
          },
        },
      },
      md: {
        item: {
          alignItems: 'center',
          // The label line (20px) alone is shorter than the designed 40px row.
          minHeight: '40',
          paddingY: '8',
          textStyle: 'body/md/regular',
          '&[data-helper]': {
            _icon: {
              // Optically center the 16px icon against the 20px label line.
              marginTop: '2',
            },
            alignItems: 'flex-start',
            paddingY: '6',
          },
        },
      },
    },
  },
  base: {
    content: {
      // Sit above the legacy v1 full-dialog/drawer layer (zIndex.fullDialog = 1200); the menu
      // portals out, so without this it renders behind a v1 FloatingDrawer. Matches the content-slot
      // z-index convention of the other floating recipes (Popover, Tooltip, Combobox/Select).
      zIndex: 'popover',
      borderRadius: '4',
      paddingY: '8',
      border: '1px solid',
      borderColor: 'border/minimal',
      _focusVisible: { outline: 'none' },
      boxShadow: 'elevation/lg',
      background: 'background/primary',
      maxHeight: 'var(--available-height)',
      // The cap is useless without these: the positioner sets --available-height from the viewport,
      // and a longer list then spills out of the content box instead of scrolling inside it.
      overflow: 'hidden',
      overflowY: 'auto',
      _open: {
        animationStyle: 'slide-fade-in',
        animationDuration: 'fast',
      },
      _closed: {
        animationStyle: 'slide-fade-out',
        animationDuration: 'faster',
      },
    },
    itemHelper: {
      textStyle: 'body/sm/regular',
      color: 'text/secondary',
    },
    item: {
      _icon: {
        color: 'icon/secondary',
        _active: {
          color: 'icon/primary',
        },
      },
      cursor: 'pointer',
      display: 'flex',
      gap: '12',
      userSelect: 'none',
      flexDirection: 'row',
      paddingLeft: '16',
      paddingRight: '24',
      _disabled: {
        cursor: 'not-allowed',
      },
      '&[data-danger]': {
        color: 'button/danger-secondary/fg',
        _icon: {
          color: 'button/danger-secondary/fg',
        },
        _active: itemActiveDanger,
        _highlighted: {
          background: 'button/danger-secondary/bg-hover',
          color: 'button/danger-secondary/fg-hover',
          _active: itemActiveDanger,
          _icon: {
            color: 'button/danger-secondary/fg-hover',
          },
        },
      },
      _active: itemActive,
      _highlighted: {
        background: 'button/secondary/bg-hover',
        _active: itemActive,
      },
      // The "leaves this page" arrow is quieter than the leading icon, and stays that way in every
      // state — including the destructive one, which overrides `_icon` above.
      '& [data-suffix-icon]': {
        color: 'icon/tertiary',
      },
    },
    itemGroupLabel: {
      // The rule after the label is a Chakra Separator, whose recipe base is `border/regular` — a
      // shade darker than the menu's own separator. Match `BitkitActionMenu.Separator`.
      '& .separator': {
        borderColor: 'border/minimal',
      },
      textStyle: 'heading/h6',
      color: 'text/tertiary',
      paddingTop: '12',
      paddingLeft: '16',
      paddingRight: '16',
      paddingBottom: '6',
      display: 'flex',
      gap: '8',
      flexDirection: 'row',
      alignItems: 'center',
    },
    separator: {
      color: 'border/minimal',
      marginY: '8',
    },
  },
  defaultVariants: {
    size: 'lg',
  },
});

export default menuSlotRecipe;
